/**
 * Dev-sync J1 — the chart legend (#psy-legend).
 *
 * Port of legacy renderLegend (psychrometric_chart.js:1033-1068). Two
 * columns: left is what a marker's COLOR means (season / missing data /
 * annotation), right is what a line's STYLE means (air stream) — independent
 * encodings, laid out side by side. All class names verbatim; the 114 psy-
 * rules already merged into common.css style this for free (survey §C2).
 *
 * The right column's three rows gained a checkbox each (the 2026-10 request):
 * the air-stream key doubles as the control for showing that stream on the
 * chart. Each row is a <label> wrapping its own checkbox, so the swatch and
 * the word toggle it too, and no row needs an id/htmlFor pair. The left
 * column is pure key — a colour is not something there is anything to turn
 * off — so its rows stay plain divs.
 *
 * Legacy fills the template's empty #psy-legend div only from renderChart
 * (data present) — with no data this renders the same empty div.
 */

import type { PsyChartData, PsySeasonFilter } from './psychrometricApi';
import { hasMissing } from './chartTraces';
import {
  PSY_TOGGLEABLE_STREAMS,
  type PsyStreamVisibility,
  type PsyToggleableStream,
} from './streamVisibility';

/** The line-swatch class that matches each stream's Plotly dash style (see
 * addTunnelTraces' `streamDash`): Supply solid, Return dotted, Exhaust
 * dashed. */
const STREAM_SWATCH_CLASS: Record<PsyToggleableStream, string> = {
  Supply: 'psy-line-solid',
  Return: 'psy-line-dotted',
  Exhaust: 'psy-line-dashed',
};

interface LegendProps {
  data: PsyChartData | null;
  season: PsySeasonFilter;
  /** The air-stream checkboxes' current state. */
  streamVisibility: PsyStreamVisibility;
  onStreamToggle: (stream: PsyToggleableStream) => void;
}

const Legend = ({ data, season, streamVisibility, onStreamToggle }: LegendProps) => {
  if (!data) return <div id="psy-legend" />;

  const seasons = season !== 'both' ? [season] : (['summer', 'winter'] as const);

  return (
    <div id="psy-legend">
      <div className="psy-legend-columns">
        <div className="psy-legend-col">
          {seasons.map((seasonKey) => (
            <div className="psy-legend-row" key={seasonKey}>
              <span
                className={`psy-swatch ${
                  seasonKey === 'winter' ? 'psy-swatch-winter' : 'psy-swatch-summer'
                }`}
              />{' '}
              {seasonKey.charAt(0).toUpperCase() + seasonKey.slice(1)}
            </div>
          ))}
          {/* Gated on a gray/open marker actually being drawn — a clamped
              ENTERING point — not the broader "not fully rated" (js:1070-1078). */}
          {hasMissing(data) && (
            <div className="psy-legend-row">
              <span className="psy-swatch psy-swatch-missing" /> Data not rated
            </div>
          )}
          <div className="psy-legend-row">
            <span className="psy-swatch psy-swatch-annotation" /> User annotation
          </div>
        </div>
        <div className="psy-legend-col">
          {PSY_TOGGLEABLE_STREAMS.map((stream) => (
            <label className="psy-legend-row psy-legend-row-toggle" key={stream}>
              {/* `clickable` exempts it from
                  `body.ahu-view-permission input { pointer-events: none;
                  opacity: .6 }` (static/css/ahu/styles.css). A view-only user
                  should be able to work this one: it writes nothing to the
                  unit, it only filters what this chart draws, and the choice
                  lives in their own localStorage. Without the exemption they
                  got the worst of both — a box dimmed as if disabled that the
                  surrounding <label> still toggled, since pointer-events on
                  the input does not stop label activation. */}
              <input
                type="checkbox"
                className="psy-stream-toggle clickable"
                checked={streamVisibility[stream]}
                onChange={() => onStreamToggle(stream)}
                aria-label={`Show ${stream} air stream`}
              />
              <span className={`psy-line-swatch ${STREAM_SWATCH_CLASS[stream]}`} /> {stream}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Legend;
